import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { toast } from 'sonner';
import { API } from '../App';
import api from '../services/api';

const PhotoOptimization = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        navigate('/login');
        return;
      }

      const response = await api.get(`${API}/auth/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      // Only super admin can access
      if (response.data.role !== 'super_admin') {
        toast.error('Access denied: Super admin only');
        navigate('/settings');
        return;
      }

      setUser(response.data);
    } catch (error) {
      console.error('Auth error:', error);
      navigate('/login');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-xl">Loading...</div>
      </div>
    );
  }

  const codeBlocks = [
    {
      title: 'Backend: Photo Manager Module (photo_manager.py)',
      description: 'Core photo management system with GridFS storage and compression',
      code: `"""
Photo Management System with GridFS and Compression
Handles photo storage, compression, and retrieval for transport log entries
"""
import io
import base64
from datetime import datetime, timezone
from typing import Optional, Dict
from PIL import Image
from motor.motor_asyncio import AsyncIOMotorGridFSBucket
import logging

logger = logging.getLogger(__name__)

class PhotoManager:
    """Manages photo storage using GridFS with compression"""
    
    def __init__(self, db):
        self.db = db
        self.fs_bucket = AsyncIOMotorGridFSBucket(db, bucket_name="photos")
        self.max_dimension = 1920  # Max width or height
        self.compression_quality = 85  # JPEG quality (1-100)
        self.thumbnail_size = (300, 300)  # Thumbnail dimensions
        
    async def compress_and_store_photo(
        self, 
        photo_data: str, 
        entry_id: str, 
        date: str,
        metadata: Optional[Dict] = None
    ) -> str:
        """Compress photo and store in GridFS"""
        try:
            # Remove data URL prefix if present
            if "," in photo_data:
                photo_data = photo_data.split(",")[1]
            
            # Decode base64
            image_bytes = base64.b64decode(photo_data)
            
            # Open image with Pillow
            image = Image.open(io.BytesIO(image_bytes))
            
            # Convert RGBA to RGB if necessary
            if image.mode in ('RGBA', 'LA', 'P'):
                background = Image.new('RGB', image.size, (255, 255, 255))
                if image.mode == 'P':
                    image = image.convert('RGBA')
                background.paste(image, mask=image.split()[-1] if image.mode in ('RGBA', 'LA') else None)
                image = background
            
            # Resize if too large
            if max(image.size) > self.max_dimension:
                ratio = self.max_dimension / max(image.size)
                new_size = tuple(int(dim * ratio) for dim in image.size)
                image = image.resize(new_size, Image.Resampling.LANCZOS)
            
            # Compress to bytes
            output = io.BytesIO()
            image.save(output, format='JPEG', quality=self.compression_quality, optimize=True)
            compressed_bytes = output.getvalue()
            
            # Prepare metadata
            file_metadata = {
                "entry_id": entry_id,
                "date": date,
                "uploaded_at": datetime.now(timezone.utc).isoformat(),
                "original_size": len(image_bytes),
                "compressed_size": len(compressed_bytes),
                "dimensions": f"{image.size[0]}x{image.size[1]}",
                "content_type": "image/jpeg"
            }
            
            if metadata:
                file_metadata.update(metadata)
            
            # Store in GridFS
            file_id = await self.fs_bucket.upload_from_stream(
                filename=f"{date}_{entry_id}.jpg",
                source=compressed_bytes,
                metadata=file_metadata
            )
            
            logger.info(
                f"Photo stored: {file_id}, "
                f"Original: {len(image_bytes)/1024:.1f}KB, "
                f"Compressed: {len(compressed_bytes)/1024:.1f}KB"
            )
            
            return str(file_id)
            
        except Exception as e:
            logger.error(f"Error compressing/storing photo: {str(e)}")
            raise`
    },
    {
      title: 'Backend: Photo API Endpoints (server.py)',
      description: 'RESTful API endpoints for photo upload, retrieval, and management',
      code: `@api_router.post("/photos/upload")
async def upload_photo(
    photo_data: dict,
    current_user: User = Depends(get_current_user)
):
    """
    Upload and compress a photo, store in GridFS
    
    Returns photo_id for reference in entry documents
    """
    try:
        photo_base64 = photo_data.get("photo")
        entry_id = photo_data.get("entry_id")
        date_str = photo_data.get("date")
        metadata = photo_data.get("metadata", {})
        
        if not photo_base64 or not entry_id or not date_str:
            raise HTTPException(status_code=400, detail="Missing required fields")
        
        # Compress and store photo
        photo_id = await photo_manager.compress_and_store_photo(
            photo_base64, 
            entry_id, 
            date_str,
            metadata
        )
        
        return {
            "photo_id": photo_id,
            "message": "Photo uploaded successfully"
        }
        
    except Exception as e:
        logging.error(f"Error uploading photo: {str(e)}")
        raise HTTPException(status_code=500, detail=f"Error uploading photo: {str(e)}")

@api_router.get("/photos/{photo_id}")
async def get_photo(
    photo_id: str,
    format: str = "binary",  # "binary" or "base64"
    current_user: User = Depends(get_current_user)
):
    """Retrieve a photo by ID with optional format"""
    try:
        if format == "base64":
            base64_data = await photo_manager.get_photo_as_base64(photo_id)
            if not base64_data:
                raise HTTPException(status_code=404, detail="Photo not found")
            return {"photo": base64_data}
        else:
            # Return binary image
            photo_bytes = await photo_manager.get_photo(photo_id)
            if not photo_bytes:
                raise HTTPException(status_code=404, detail="Photo not found")
            
            return Response(
                content=photo_bytes,
                media_type="image/jpeg",
                headers={"Cache-Control": "public, max-age=31536000"}
            )
            
    except HTTPException:
        raise
    except Exception as e:
        logging.error(f"Error retrieving photo: {str(e)}")
        raise HTTPException(status_code=500, detail=f"Error retrieving photo: {str(e)}")`
    },
    {
      title: 'Backend: Data Model Updates (server.py)',
      description: 'Updated DeliveryEntry model to support photo_id references',
      code: `class DeliveryEntry(BaseModel):
    id: str = Field(default_factory=lambda: str(uuid.uuid4()))
    customer_name: str = ""
    location: str = ""
    postcode: str = ""
    order_number: str = ""
    weight: float = 0.0
    miles: float = 0.0
    status: str = "pending"
    status_note: str = ""
    time_slot: str = ""
    notes: str = ""
    contribution: float = 0.0
    sales_order_value: float = 0.0
    photo: Optional[str] = None  # Legacy: Base64 (deprecated)
    photo_id: Optional[str] = None  # GridFS file ID for photo`
    },
    {
      title: 'Frontend: Lazy Loading Implementation (ViewModeGrid.jsx)',
      description: 'Photos load only when modal opens, with loading state',
      code: `// Open entry modal with lazy photo loading
const openEntryModal = async (entry, vehicle, runId = null) => {
  setSelectedEntry(entry);
  setSelectedVehicle(vehicle);
  setSelectedRunId(runId);
  
  // Lazy load photo if photo_id exists
  if (entry.photo_id) {
    setPhotoLoading(true);
    try {
      const response = await api.get(\`\${API}/photos/\${entry.photo_id}?format=base64\`);
      setEntryPhoto(response.data.photo);
    } catch (error) {
      console.error('Error loading photo:', error);
      setEntryPhoto(null);
    } finally {
      setPhotoLoading(false);
    }
  } else if (entry.photo) {
    // Legacy: Load from base64 (if old format)
    setEntryPhoto(entry.photo);
  } else {
    setEntryPhoto(null);
  }
  
  setShowEntryModal(true);
};`
    },
    {
      title: 'Frontend: Photo Upload with Compression (ViewModeGrid.jsx)',
      description: 'Client-side photo upload that triggers backend compression',
      code: `// Upload photo to backend (GridFS)
const uploadPhotoToBackend = async (photoData) => {
  if (!selectedEntry || !selectedVehicle) return;

  try {
    const dateStr = format(selectedDate, 'yyyy-MM-dd');
    
    // Upload photo to GridFS
    const uploadResponse = await api.post(\`\${API}/photos/upload\`, {
      photo: photoData,
      entry_id: selectedEntry.id,
      date: dateStr,
      metadata: {
        customer_name: selectedEntry.customer_name,
        order_number: selectedEntry.order_number,
        vehicle_id: selectedVehicle.id
      }
    });
    
    const photoId = uploadResponse.data.photo_id;
    
    // Update entry with photo_id reference
    const response = await api.get(\`\${API}/logs/\${dateStr}\`);
    const dailyLog = response.data;

    const updatedVehicles = dailyLog.vehicles.map(v => {
      if (v.id === selectedVehicle.id) {
        let updatedVehicle = { ...v };
        
        if (selectedRunId) {
          updatedVehicle.runs = v.runs.map(run => {
            if (run.id === selectedRunId) {
              return {
                ...run,
                entries: run.entries.map(e => 
                  e.id === selectedEntry.id ? { ...e, photo_id: photoId, photo: null } : e
                )
              };
            }
            return run;
          });
        } else {
          updatedVehicle.entries = v.entries.map(e =>
            e.id === selectedEntry.id ? { ...e, photo_id: photoId, photo: null } : e
          );
        }
        
        return updatedVehicle;
      }
      return v;
    });

    await api.put(\`\${API}/logs/\${dateStr}\`, {
      date: dateStr,
      vehicles: updatedVehicles
    });

    setSelectedEntry({ ...selectedEntry, photo_id: photoId, photo: null });
    toast.success('Photo saved successfully');
  } catch (error) {
    console.error('Error uploading photo:', error);
    toast.error('Failed to save photo');
  }
};`
    },
    {
      title: 'Frontend: Photo Loading Indicator (ViewModeGrid.jsx)',
      description: 'Visual feedback during photo upload and loading',
      code: `{/* Photo Upload Box with Loading State */}
<div 
  onClick={() => photoLoading ? null : (entryPhoto ? setShowPhotoOptions(true) : triggerPhotoInput())}
  className="w-24 h-24 md:w-28 md:h-28 border-2 border-dashed rounded-lg flex items-center justify-center cursor-pointer hover:border-orange-500 transition-colors overflow-hidden relative"
  style={{ borderColor: entryPhoto ? enconOrange : '#d1d5db' }}
>
  {photoLoading ? (
    <div className="text-center">
      <svg className="animate-spin w-8 h-8 mx-auto text-orange-500" fill="none" viewBox="0 0 24 24">
        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
      </svg>
      <span className="text-[10px] text-gray-500 mt-1">Loading...</span>
    </div>
  ) : entryPhoto ? (
    <img src={entryPhoto} alt="Entry" className="w-full h-full object-cover" />
  ) : (
    <div className="text-center p-2">
      <svg className="w-8 h-8 mx-auto mb-1 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
      </svg>
      <span className="text-[10px] text-gray-500">Add Photo</span>
    </div>
  )}
</div>`
    }
  ];

  return (
    <div className="min-h-screen bg-gray-50 p-4">
      {/* Header */}
      <div className="max-w-7xl mx-auto mb-6">
        <Button
          onClick={() => navigate('/settings')}
          variant="ghost"
          className="mb-4"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Settings
        </Button>

        <div className="bg-white rounded-lg shadow-lg p-6 border-l-4 border-orange-500">
          <h1 className="text-3xl font-bold mb-4 text-gray-800">
            Photo Optimization System Documentation
          </h1>
          
          {/* Developer Attribution */}
          <div className="bg-gradient-to-r from-orange-50 to-amber-50 p-6 rounded-lg border-2 border-orange-200 mb-6">
            <h2 className="text-xl font-bold text-orange-800 mb-3">
              Developed, Coded and Designed by Jamie Moseley
            </h2>
            <p className="text-sm text-gray-700 mb-2">
              <strong>Contact:</strong> moseley_jj@hotmail.com
            </p>
            
            <div className="mt-4 p-4 bg-white rounded border border-orange-300">
              <h3 className="font-semibold text-gray-800 mb-2">Copyright Notice - Photo Optimization System</h3>
              <p className="text-sm text-gray-700 leading-relaxed mb-3">
                All code contained within this photo optimization system, including but not limited to the frontend components, 
                backend API endpoints, database schemas, compression algorithms, GridFS implementation, lazy loading mechanisms, 
                and related infrastructure is the exclusive intellectual property of <strong>Jamie Moseley</strong>.
              </p>

              <p className="text-sm text-gray-700 leading-relaxed mb-3 bg-purple-50 p-3 rounded border border-purple-200">
                <strong>Development Context:</strong> This photo optimization system has been developed primarily during the 
                developer's own time, with testing and implementation conducted during scheduled work periods as explicitly 
                agreed with the Branch Director. Under employment contract clause 18.1 (Patent Rights), the developer retains 
                full intellectual property rights as the employing company has not expressed direct interest in ownership.
              </p>

              <p className="text-sm text-gray-700 leading-relaxed mb-3 bg-orange-50 p-3 rounded border border-orange-200">
                <strong>Usage & Licensing Terms:</strong> Use of this photo optimization system is permitted only as explicitly 
                agreed by Jamie Moseley. Any deployment or operational use is instantly chargeable at amounts specified by the 
                developer. All fees must cover development costs, ongoing maintenance, and intellectual property value unless 
                a formal sale agreement is executed between the developer and the employing company.
              </p>
              
              <p className="text-sm text-gray-700 leading-relaxed mb-3">
                This code and all associated documentation may not be copied, reproduced, distributed, modified, or used in 
                any form—in whole or in part—without explicit written permission from the copyright holder. Unauthorized use, 
                replication, or distribution of this code in its exact form or derivative works is strictly prohibited and may 
                result in legal action under copyright, patent, and intellectual property laws of England and Wales, United Kingdom.
              </p>
              
              <p className="text-xs text-gray-600 mt-3 pt-3 border-t border-orange-200">
                © 12th May 2018 - Present, Jamie Moseley. All rights reserved.<br />
                <strong>Full Legal Agreement:</strong> <a href="/terms-conditions.html" target="_blank" className="text-orange-600 underline hover:text-orange-800">View Terms & Conditions (includes Copyright & Patent Agreement)</a>
              </p>
            </div>
          </div>

          {/* Feature Overview */}
          <div className="prose max-w-none">
            <h3 className="text-xl font-semibold mb-3">System Overview</h3>
            <p className="text-gray-700 mb-4">
              This advanced photo storage optimization system replaces the previous base64 inline storage approach with 
              a professional-grade solution using MongoDB GridFS and intelligent image compression. The system is designed 
              to handle 30+ photos per day over 24+ months with optimal performance and scalability.
            </p>

            <div className="grid md:grid-cols-2 gap-4 mb-6">
              <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                <h4 className="font-semibold text-green-800 mb-2">✅ Benefits Achieved</h4>
                <ul className="text-sm text-gray-700 space-y-1">
                  <li>• 70% storage reduction through compression</li>
                  <li>• Eliminated 16MB document size limitations</li>
                  <li>• Faster page loads with lazy loading</li>
                  <li>• Browser caching for improved performance</li>
                  <li>• Automated cleanup for old photos</li>
                  <li>• Comprehensive metadata tracking</li>
                </ul>
              </div>

              <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                <h4 className="font-semibold text-blue-800 mb-2">📊 Storage Optimization</h4>
                <ul className="text-sm text-gray-700 space-y-1">
                  <li>• <strong>Before:</strong> ~15MB/day (30 photos × 500KB)</li>
                  <li>• <strong>After:</strong> ~4.5MB/day (30 photos × 150KB)</li>
                  <li>• <strong>24 months total:</strong> ~3.3GB vs ~11GB</li>
                  <li>• <strong>Compression:</strong> JPEG quality 85%</li>
                  <li>• <strong>Max dimension:</strong> 1920px</li>
                  <li>• <strong>Storage type:</strong> MongoDB GridFS</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Code Blocks */}
      <div className="max-w-7xl mx-auto space-y-4">
        {codeBlocks.map((block, index) => (
          <Card key={index}>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <span className="bg-orange-100 text-orange-700 px-2 py-1 rounded text-sm font-mono">
                  {index + 1}
                </span>
                {block.title}
              </CardTitle>
              <p className="text-sm text-gray-600 mt-2">{block.description}</p>
            </CardHeader>
            <CardContent>
              <pre className="bg-gray-900 text-gray-100 p-4 rounded-lg overflow-x-auto text-sm">
                <code>{block.code}</code>
              </pre>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Footer */}
      <div className="max-w-7xl mx-auto mt-8 text-center text-sm text-gray-500">
        <p>This documentation is accessible only to super administrators.</p>
        <p className="mt-2">© 12th May 2018 - Present, Jamie Moseley. All rights reserved.</p>
      </div>
    </div>
  );
};

export default PhotoOptimization;
